import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useCalculators from '../hooks/useCalculators';
import './RetirementPlanningSeries.css';
import './CalculatorToolsRail.css';
import '../pages/Home.css';

function ToolsCarousel({
  tools,
  currentToolKey,
  navigate,
  groupId,
  headingId,
}) {
  const viewportRef = useRef(null);
  const currentSlideRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const list = Array.isArray(tools) ? tools : [];
  const hasCurrent = list.some((tool) => tool.slug === currentToolKey);

  const updateNavState = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const eps = 4;
    setCanPrev(scrollLeft > eps);
    setCanNext(scrollLeft + clientWidth < scrollWidth - eps);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el || list.length === 0) return undefined;
    updateNavState();
    el.addEventListener('scroll', updateNavState, { passive: true });
    const ro = new ResizeObserver(updateNavState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateNavState);
      ro.disconnect();
    };
  }, [list.length, updateNavState]);

  useEffect(() => {
    if (!hasCurrent) return undefined;
    const vp = viewportRef.current;
    const currentEl = currentSlideRef.current;
    if (!vp || !currentEl) return undefined;

    const frame = requestAnimationFrame(() => {
      const targetLeft =
        currentEl.offsetLeft - (vp.clientWidth - currentEl.offsetWidth) / 2;
      const maxLeft = Math.max(0, vp.scrollWidth - vp.clientWidth);
      const nextLeft = Math.min(Math.max(0, targetLeft), maxLeft);
      vp.scrollTo({ left: nextLeft, behavior: 'smooth' });
    });

    return () => cancelAnimationFrame(frame);
  }, [currentToolKey, list.length, hasCurrent]);

  const getScrollStep = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return 0;
    const slide = vp.querySelector('.rps-series__slide');
    if (!slide) return vp.clientWidth * 0.28;
    const track = vp.querySelector('.rps-series__track');
    const gap = track
      ? parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16
      : 16;
    return slide.getBoundingClientRect().width + gap;
  }, []);

  const scrollPrev = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    vp.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
  }, [getScrollStep]);

  const scrollNext = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    vp.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
  }, [getScrollStep]);

  if (list.length === 0) return null;

  return (
    <div
      className="rps-series__carousel-row calc-tools-rail__group-carousel"
      role={list.length > 1 ? 'group' : undefined}
      aria-labelledby={headingId}
      data-group={groupId}
    >
      {list.length > 1 && (
        <button
          type="button"
          className="rps-series__nav-btn rps-series__nav-btn--prev"
          onClick={scrollPrev}
          disabled={!canPrev}
          aria-label="Previous tools"
        >
          <span className="rps-series__nav-icon" aria-hidden>
            ‹
          </span>
        </button>
      )}

      <div className="rps-series__viewport" ref={viewportRef}>
        <div className="rps-series__track">
          {list.map((tool) => {
            const isCurrent = tool.slug === currentToolKey;

            return (
              <div
                key={tool.slug}
                className={`rps-series__slide${isCurrent ? ' rps-series__slide--current' : ''}`}
                ref={isCurrent ? currentSlideRef : null}
              >
                {isCurrent && (
                  <span className="rps-series__current-badge" aria-label="Current tool">
                    You are here
                  </span>
                )}
                <div className="tool-card">
                  <div className="tool-icon">
                    <i className={tool.icon}></i>
                  </div>
                  <h3>{tool.title}</h3>
                  <p>{tool.description}</p>
                  <button
                    type="button"
                    className="tool-btn"
                    onClick={() => navigate(tool.route_path)}
                    disabled={isCurrent}
                  >
                    {isCurrent ? 'Currently in Use' : 'Calculate'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {list.length > 1 && (
        <button
          type="button"
          className="rps-series__nav-btn rps-series__nav-btn--next"
          onClick={scrollNext}
          disabled={!canNext}
          aria-label="Next tools"
        >
          <span className="rps-series__nav-icon" aria-hidden>
            ›
          </span>
        </button>
      )}
    </div>
  );
}

const CalculatorToolsRail = ({ currentToolKey }) => {
  const navigate = useNavigate();
  const { groups, calculators } = useCalculators();

  const orderedGroups = useMemo(() => {
    const nonempty = groups.filter((g) => g.tools?.length > 0);
    if (!nonempty.length) return nonempty;
    const activeGroupId = calculators.find(
      (tool) => tool.slug === currentToolKey,
    )?.group;
    if (!activeGroupId) return nonempty;
    const active = nonempty.filter((g) => g.id === activeGroupId);
    const rest = nonempty.filter((g) => g.id !== activeGroupId);
    return [...active, ...rest];
  }, [groups, calculators, currentToolKey]);

  return (
    <section
      className="rps-series calc-tools-rail"
      aria-labelledby="calc-tools-rail-heading"
    >
      <div className="rps-series__head">
        <div className="rps-series__titles">
          <h2 id="calc-tools-rail-heading">Financial Planning Tools</h2>
          <p className="rps-series__subtitle">
            Essential calculators and tools for your wealth management journey
          </p>
        </div>
      </div>

      {orderedGroups.length > 0 ? (
        <div className="calc-tools-rail__groups">
          {orderedGroups.map((group) => {
            const headingId = `calc-tools-rail-${group.id}`;
            return (
              <div className="calc-tools-rail__group" key={group.id}>
                <h3 className="calc-tools-rail__group-title" id={headingId}>
                  {group.title}
                </h3>
                <ToolsCarousel
                  tools={group.tools}
                  currentToolKey={currentToolKey}
                  navigate={navigate}
                  groupId={group.id}
                  headingId={headingId}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rps-series__empty" role="status">
          <p>Financial tools will appear here once calculators are available.</p>
        </div>
      )}
    </section>
  );
};

export default CalculatorToolsRail;
